/**
 * NotificationsPage - Full notification list page
 *
 * Matches Xamarin NotificationListPage + NotificationListMobile (IsPage=true mode):
 * - OnNavigatedTo: Subscribe to events, init filter tabs (All/Task/Trailer), load notifications
 * - Filter tabs: All, Task, Trailer — triggers API re-fetch with filter param
 * - Notification list: Task notifications + Trailer notifications
 * - Empty state: no_notification.svg + "No Notification!" + "There are no notifications..."
 * - Loading state: spinner while fetching
 * - Navigation: Task notifications → Dashboard, Trailer notifications → Trailers page
 * - Destroy: Save lastNotificationTime, clear unread list
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  EmptyNotificationState,
  NotificationFilterTabs,
  TaskNotificationCard,
  TrailerNotificationCard,
} from '@/components/notification';
import { NOTIFICATION_FILTER, ROUTES } from '@/constants';
import { useNotificationsQuery } from '@/hooks/dashboard';
import { setLastNotificationTime, clearUnreadNotificationIds } from '@/utils';

import { Spinner } from './LoadingPage';

import type { NotificationFilterType } from '@/constants';
import type { NotificationModel } from '@/types';

const FILTER_OPTIONS: NotificationFilterType[] = [
  NOTIFICATION_FILTER.ALL,
  NOTIFICATION_FILTER.TASK,
  NOTIFICATION_FILTER.TRAILER,
];

export function NotificationsPage() {
  const navigate = useNavigate();

  const [selectedFilter, setSelectedFilter] = useState<NotificationFilterType>(
    NOTIFICATION_FILTER.ALL
  );

  // Matches Xamarin NotificationListViewModel.ShowDefaultNotifications / ShowAllNotifications
  const { data: notifications = [], isLoading } = useNotificationsQuery(selectedFilter, true);

  // Matches Xamarin NotificationListViewModel.Destroy():
  // PreferencesService.SetLastNotificationTime(DateTime.Now) + clear unreads
  useEffect(() => {
    return () => {
      setLastNotificationTime(new Date().toISOString());
      clearUnreadNotificationIds();
    };
  }, []);

  // Matches Xamarin SelectedFilterCommandHandler
  const handleFilterChange = useCallback((filter: NotificationFilterType) => {
    setSelectedFilter(filter);
  }, []);

  // Matches Xamarin NavigateToUpcomingTasksHandler
  const handleNotificationPress = useCallback(
    (notification: NotificationModel) => {
      if (notification.notificationType === 'Task') {
        navigate(ROUTES.dashboard);
      } else {
        navigate(ROUTES.trailers);
      }
    },
    [navigate]
  );

  const isEmpty = !isLoading && notifications.length === 0;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white p-2.5">
      {/* Filter tabs — Matches Xamarin CollectionView filterList */}
      <div className="shrink-0">
        <NotificationFilterTabs
          filters={FILTER_OPTIONS}
          selectedFilter={selectedFilter}
          onFilterChange={handleFilterChange}
        />
      </div>

      {/* Content area — flex-1 + min-h-0 prevents content from expanding beyond viewport */}
      <div className="mt-2.5 flex-1 min-h-0 flex flex-col">
        {isLoading && (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        )}

        {isEmpty && <EmptyNotificationState />}

        {!isLoading && !isEmpty && (
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col gap-[5px]">
              {notifications.map((notification) => {
                if (notification.isShowTaskNotification) {
                  return (
                    <TaskNotificationCard
                      key={notification.id}
                      notification={notification}
                      onPress={handleNotificationPress}
                    />
                  );
                }
                if (notification.isShowTrailerNotification) {
                  return (
                    <TrailerNotificationCard
                      key={notification.id}
                      notification={notification}
                      onPress={handleNotificationPress}
                    />
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
